<template>
  <div>
    <!-- Hero -->
    <section
      class="relative overflow-hidden border-b border-border  bg-gradient-to-b from-[color-mix(in_srgb,var(--primary)_4%,transparent)] to-transparent pb-16 pt-16 md:pb-22 md:pt-24">
      <FloatingGlyphs />
      <div class="relative mx-auto max-w-[var(--container)] flex justify-center px-6">
        <div class="max-w-2xl flex flex-col items-center">
          <Commerce-Badge tone="success" variant="outline" class="mb-4 nums">{{ fa(50) }}+ سرویس در
            دسترس</Commerce-Badge>
          <h1 class="text-3xl font-extrabold leading-[1.2] text-center text-nowrap md:text-5xl">پرداخت سرویس‌های
            <br class="block md:hidden" />بی‌المللی
          </h1>
          <p class="mt-4 max-w-lg text-base leading-relaxed text-center text-muted-foreground md:text-lg">
            اکانت، اشتراک، گیفت‌کارت یا پرداخت دیجیتال <br class="block md:hidden" /> موردنظرتان را انتخاب کنید و سفارش
            خود را آنلاین<br class="block md:hidden" /> ثبت کنید.
          </p>
          <div class="w-full mt-6">
            <ServiceSearch placeholder="نام سرویس، ابزار یا گیفت‌کارت را جست‌وجو کنید" />
          </div>
          <ul class="mt-6 flex flex-wrap justify-center items-center  gap-5 text-sm text-muted-foreground mx-4 md:mx-0 "
            v-if="trust.length">
            <li v-for="t in trust" :key="t" class="flex items-center gap-1.5">
              <span class="text-primary">✓</span>
              {{ t }}
            </li>
          </ul>
        </div>
      </div>
      <!-- Entry cards -->
      <div class="mx-auto max-w-[var(--container)] px-6 mt-10 relative z-10 ">
        <ul class="grid gap-4 lg:grid-cols-3">
          <li v-for="card in entryCards" :key="card.title">
            <nuxt-link :to="card.link">
              <Commerce-Card clickable class="flex items-center justify-between py-6 lg:py-5 p-5">
                <div class="flex items-center gap-3">
                  <div
                    class="grid h-13 w-13 place-items-center rounded-[var(--radius-md)] bg-secondary text-2xl text-primary">
                    {{ card.glyph }}
                  </div>
                  <div>
                    <h3 class="text-base font-bold">{{ card.title }}</h3>
                    <p class="mt-0.5 text-sm text-muted-foreground">{{ card.body }}</p>
                  </div>
                </div>
                <span class="text-primary">›</span>
              </Commerce-Card>
            </nuxt-link>
          </li>
        </ul>
      </div>
    </section>

    <!-- Popular services -->
    <section class="mx-auto mt-12 max-w-[var(--container)] px-6">
      <div class="flex items-center justify-between">
        <h2 class=" font-extrabold text-xl">سرویس‌های محبوب</h2>
        <NuxtLink to="/all">
          <Commerce-Button variant="ghost" size="sm">
            مشاهده همه ›
          </Commerce-Button>
        </NuxtLink>
      </div>
      <div v-show="loadingFeaturedServices" class="mt-4 grid gap-4 sm:grid-cols-2">
        <CardSkeleton v-for="i in 8" :key="i" variant="service" />
      </div>
      <div class="mt-4 grid gap-4 sm:grid-cols-2">
        <ServiceCard v-for="s in popularServices" :key="s.id" :service="s" />
      </div>
    </section>

    <!-- Service Category Cards -->
    <section class="mx-auto max-w-[var(--container)] px-6 mt-24 relative z-10 ">
      <h2 class=" font-extrabold text-xl mb-4">دسته‌بندی خدمات</h2>
      <div v-if="loadingCategories" class="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        <CardSkeleton v-for="i in 6" :key="i" variant="category" />
      </div>
      <ul v-else class="grid gap-3 md:grid-cols-2 lg:grid-cols-3 ">
        <li v-for="card in categories" :key="card.id">
          <NuxtLink :to="`/category/${card.slug}`">
            <Commerce-Card clickable class="flex items-center justify-between p-4 lg:p-3">
              <div class="flex items-center gap-3">
                <Commerce-ServiceGlyph :glyph="card.glyph" size="sm" />
                <div>
                  <h3 class=" font-bold text-base">{{ card.name }}</h3>
                  <p class="mt-0.5 text-xs text-muted-foreground nums"> {{ fa(card.servicesCount ?? 0) }} سرویس </p>
                </div>
              </div>
              <span class="text-muted-foreground">›</span>
            </Commerce-Card>
          </NuxtLink>
        </li>
      </ul>
    </section>

    <!-- Place a Custom Order -->
    <section class="mx-auto mt-20 max-w-[var(--container)] px-6">
      <Commerce-Card class="flex flex-col lg:flex-row lg:items-center justify-between p-8 gap-4 lg:gap-0">
        <div class="flex items-center gap-3">
          <div>
            <h2 class=" font-bold text-lg">سرویس موردنظرتان را پیدا نکردید؟</h2>
            <p class="mt-2 text-sm lg:text-xs leading-6 lg:leading-5 text-justify text-muted-foreground nums">
              اگر پرداخت شما در فهرست نیست، مشخصات آن را ارسال کنید تا پس از بررسی، مبلغ و امکان انجام سفارش به شما
              اعلام <br class="hidden lg:block" />شود.
            </p>
          </div>
        </div>
        <NuxtLink to="/custom">
          <Commerce-Button size="md" variant="primary">ثبت سفارش اختصاصی</Commerce-Button>
        </NuxtLink>

      </Commerce-Card>
    </section>

    <!-- Steps -->
    <section class="mx-auto mt-20 max-w-[var(--container)] px-6">
      <h2 class="font-extrabold text-xl">فرآیند در چهار مرحله</h2>
      <ol class="mt-4 grid gap-4 lg:grid-cols-4">
        <li v-for="step in steps" :key="step.n">
          <Commerce-card class="flex flex-col justify-center items-start p-6 lg:p-4">
            <div
              class=" grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground text-xs font-black nums">
              {{ fa(step.n) }}
            </div>
            <h3 class="mt-3 font-bold">{{ step.t }}</h3>
            <p class="mt-1 text-sm lg:text-xs text-muted-foreground">{{ step.d }}</p>
          </Commerce-card>
        </li>
      </ol>
    </section>

    <!-- Transparency Item -->
    <section class="mx-auto mt-20 max-w-[var(--container)] px-6">
      <h2 class=" font-extrabold text-xl">شفافیت در هر مرحله</h2>
      <ul class="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4 ">
        <li v-for="(transparencyItem, index) in transparencyItems" :key="`item${index}`">
          <Commerce-card class="flex flex-col justify-center items-start p-6 lg:p-4">
            <Commerce-ServiceGlyph glyph="◆" size="xs" />
            <h3 class="mt-3 font-bold">{{ transparencyItem.title }}</h3>
            <p class="mt-1 text-sm lg:text-xs text-muted-foreground">{{ transparencyItem.description }}</p>
          </Commerce-card>
        </li>
      </ul>
    </section>

    <!-- FAQ -->
    <section class="mx-auto mt-20 max-w-[var(--container)] px-6 pb-16">
      <h2 class=" font-extrabold text-xl">سؤال‌های پرتکرار</h2>
      <Commerce-card class="mx-auto  max-w-3xl  divide-y divide-border   mt-8 flex flex-col gap-2 py-4 lg:py-0 ">
        <div v-for="(faq, i) in faqs" :key="i" class=" overflow-hidden">
          <button
            class="flex w-full items-center justify-between px-5 py-4 text-right font-medium transition-colors cursor-pointer"
            @click="openFaq = openFaq === i ? null : i">
            {{ faq.q }}
            <span class="text-primary transition-transform" :class="openFaq === i ? 'rotate-45' : ''">
              +
            </span>
          </button>
          <p v-if="openFaq === i" class="px-5 pb-4 text-sm text-muted-foreground leading-relaxed pj-in">
            {{ faq.a }}
          </p>
        </div>
      </Commerce-card>
    </section>
  </div>
</template>

<script setup lang="ts">
const siteUrl = useSiteUrl()

import { computed, ref } from 'vue'
import { useSeoMeta } from '#app'
import { faqs } from '~/utils/data'
import { fa } from '~/utils/format'

useSeoMeta({
  title: 'پرداخت سرویس‌های بین‌المللی - پی‌جیب',
  description: 'اکانت، اشتراک، گیفت‌کارت یا پرداخت دیجیتال موردنظرتان را انتخاب کنید و سفارش خود را آنلاین ثبت کنید.',
  robots: 'index,follow',

  ogTitle: 'پرداخت سرویس‌های بین‌المللی - پی‌جیب',
  ogDescription: 'اکانت، اشتراک، گیفت‌کارت یا پرداخت دیجیتال موردنظرتان را انتخاب کنید و سفارش خود را آنلاین ثبت کنید.',
  ogImage: siteUrl('/services/payjib.webp'),
  ogUrl: siteUrl('/services/'),
  ogType: 'website',
  ogSiteName: 'پی‌جیب',
  ogLocale: 'fa_IR',

  twitterCard: 'summary_large_image',
  twitterTitle: 'پرداخت سرویس‌های بین‌المللی - پی‌جیب',
  twitterDescription:
    'اکانت، اشتراک، گیفت‌کارت یا پرداخت دیجیتال موردنظرتان را انتخاب کنید و سفارش خود را آنلاین ثبت کنید.',
  twitterImage: siteUrl('/services/payjib.webp'),
})

// useHead({
//   link: [
//     {
//       rel: 'canonical',
//       href: siteUrl('/services'),
//     },
//   ],
// })

const router = useRouter()

const { categories: apiCategories, loadingCategories } = useCategories()
const { data: featuredService, loading: loadingFeaturedServices } = useFeaturedServices()

const categoryGlyphMap: Record<string, string> = {
  ai: '✦',
  'education-university': '◇',
  'gaming-entertainment': '▦',
  development: '◉',
  'design-content': '▣',
  business: '⌘',
  'gift-cards': '◫',
  'music-movies': '♫',
  'other-services': '＋',
}

const categories = computed(() =>
  (apiCategories.value ?? []).map((category) => ({
    ...category,
    glyph: categoryGlyphMap[category.slug] ?? '⌘',
  }))
)

function navigateTo(path: string) {
  router.push(path)
  window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
}

const openFaq = ref<number | null>(0)

const trust = ['قیمت شفاف', 'پیگیری مرحله‌به‌مرحله', 'پشتیبانی حرفه‌ای']

const entryCards = [
  { title: 'خرید و شارژ اکانت', body: 'اشتراک و اعتبار سرویس‌های بین‌المللی', glyph: '✦', link: '/all' },
  { title: 'خرید گیفت‌کارت', body: 'اپل، گوگل‌پلی، استیم و بیشتر', glyph: '◫', link: '/gift-cards' },
  { title: 'سفارش اختصاصی', body: 'هر پرداختی که در فهرست نیست', glyph: '＋', link: '/custom' },
]

const steps = [
  { n: 1, t: 'انتخاب سرویس', d: 'سرویس یا گیفت‌کارت موردنظر را پیدا کنید.' },
  { n: 2, t: 'تکمیل اطلاعات', d: 'پلن، ارز و اطلاعات موردنیاز را وارد کنید.' },
  { n: 3, t: 'پرداخت ریالی', d: 'مبلغ نهایی را با درگاه یا کیف پول بپردازید.' },
  { n: 4, t: 'انجام و اطلاع‌رسانی', d: 'سفارش حداکثر تا ۲۴ ساعت انجام می‌شود.' },
]

const transparencyItems = [
  { title: 'کارمزد جدا و مشخص', description: 'کارمزد هرگز در مبلغ اصلی پنهان نمی‌شود.' },
  { title: 'نرخ ارز لحظه‌ای', description: 'مبلغ قطعی پس از ورود با نرخ روز محاسبه می‌شود.' },
  { title: 'تفکیک کامل مبلغ', description: 'مبلغ اصلی، نرخ تبدیل، کارمزد و مالیات را می‌بینید.' },
  { title: 'بدون هزینهٔ پنهان', description: 'هزینه‌های احتمالی مانند VAT شفاف اعلام می‌شوند.' },
]

const popularServices = computed(() => featuredService.value?.slice(0, 8) ?? [])
</script>
